import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { sectorsApi } from '../api';
import { Building2 } from 'lucide-react';

/**
 * SectorFilter Component
 * 
 * Provides automatic sector filtering based on user's assigned sector.
 * - Admin users see all sectors with option to filter
 * - Non-admin users are restricted to their sector
 * 
 * Props:
 * - user: Current logged-in user object (must have sector_id, sector_name, is_admin)
 * - value: Current selected sector value
 * - onChange: Callback when sector selection changes
 * - showAllOption: Whether to show "Todos os Setores" option (default: true for admins)
 * - className: Additional CSS classes
 */
const SectorFilter = ({ 
  user, 
  value, 
  onChange, 
  showAllOption = true,
  className = '',
  label = 'Filtrar por Setor',
  sectors: propSectors
}) => {
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.is_admin || false;
  const userSectorId = user?.sector_id;
  const userSectorName = user?.sector_name;

  useEffect(() => {
    if (!propSectors) {
      loadSectors();
    } else {
      setSectors(propSectors);
      setLoading(false);
    }
  }, [propSectors]);

  // Auto-select user's sector for non-admins
  useEffect(() => {
    if (!isAdmin && userSectorName && !value) {
      onChange(userSectorName);
    }
  }, [isAdmin, userSectorName, value, onChange]);

  const loadSectors = async () => {
    try {
      const response = await sectorsApi.listActive();
      setSectors(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar setores:', error);
      toast.error('Erro ao carregar setores');
    } finally {
      setLoading(false);
    }
  };

  // Non-admins without sector can't filter
  if (!isAdmin && !userSectorId) {
    return null;
  }

  // Non-admins with sector see a read-only indicator
  if (!isAdmin && userSectorId) {
    return (
      <div className={`flex items-center gap-2 px-3 py-2 bg-blue-500/10 border border-blue-500/30 rounded-lg ${className}`}>
        <Building2 size={16} className="text-blue-400" />
        <span className="text-sm text-blue-300">
          Setor: <span className="font-medium text-white">{userSectorName}</span>
        </span>
      </div>
    );
  }

  // Admin sees full dropdown
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className={`input px-3 w-full sm:w-48 ${className}`}
      disabled={loading}
      data-testid="sector-filter"
    >
      {showAllOption && (
        <option value="">Todos os Setores</option>
      )}
      {sectors.map(sector => (
        <option key={sector.id} value={sector.name}>
          {sector.name}
        </option>
      ))}
    </select>
  );
};

/**
 * Hook for sector filtering
 * Returns the effective sector filter value and helper functions
 */
export const useSectorFilter = (user) => {
  const [sectorFilter, setSectorFilter] = useState('');
  
  const isAdmin = user?.is_admin || false;
  const userSectorName = user?.sector_name;

  // Calculate effective filter (memoized to prevent re-renders)
  const effectiveFilter = useMemo(() => {
    if (!isAdmin && userSectorName) {
      return userSectorName;
    }
    return sectorFilter;
  }, [isAdmin, userSectorName, sectorFilter]);

  // Check if user can see all records
  const canSeeAll = isAdmin && !sectorFilter;

  return {
    sectorFilter,
    setSectorFilter,
    effectiveFilter,
    canSeeAll,
    isAdmin,
    userSectorName
  };
};

export default SectorFilter;
